import type { TenantCurrency } from '@erp/shared';
import { Coins, Lock, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { useCurrenciesStore } from '../../lib/currenciesStore.js';

/**
 * Settings → Currencies. Pick which of the 20 catalogue currencies this tenant
 * actually trades in; every currency dropdown in the app (invoices, bills,
 * vouchers, journal entries, price lists, POS …) then offers only those.
 *
 * Turning one OFF only hides it from pickers — the row (and its `exponent`)
 * stays, so amounts already stored in that currency keep displaying with the
 * right number of decimals. The BASE currency can't be turned off; it's shown
 * locked here and the API refuses it too.
 */
export function CurrenciesPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('settings.write');
  const base = useAuthStore((s) => s.tenant?.baseCurrencyCode ?? '');
  const [rows, setRows] = useState<TenantCurrency[] | null>(null);
  /** Draft on/off state by code, and the last saved state behind it. */
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [baseline, setBaseline] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /** Which message to show — translated at render so load() stays hook-free. */
  const [error, setError] = useState<'load' | 'save' | null>(null);
  const [query, setQuery] = useState('');

  const load = async (): Promise<void> => {
    try {
      // ?all=1 = the whole catalogue with each row's flag (the plain call
      // returns only the active ones, which is what the pickers consume).
      const r = await api().get<{ items: TenantCurrency[] }>('/currencies', { params: { all: 1 } });
      const items = r.data.items;
      const f = Object.fromEntries(items.map((c) => [c.code, c.isActive]));
      setRows(items);
      setFlags(f);
      setBaseline(f);
    } catch {
      setError('load');
    }
  };

  useEffect(() => { void load(); }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!rows) return [];
    if (!q) return rows;
    return rows.filter((c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
  }, [rows, query]);

  const enabledCount = rows ? rows.filter((c) => flags[c.code]).length : 0;
  const dirty = rows ? rows.some((c) => flags[c.code] !== baseline[c.code]) : false;

  const toggle = (c: TenantCurrency): void => {
    if (!canWrite || c.code === base) return;
    setSaved(false);
    setFlags((f) => ({ ...f, [c.code]: !f[c.code] }));
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!rows) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    const changed = rows.filter((c) => flags[c.code] !== baseline[c.code]);
    try {
      // One PATCH per changed code, sequentially — the list is short and a
      // per-row endpoint keeps each change individually audited.
      for (const c of changed) {
        await api().patch(`/currencies/${c.code}`, { isActive: flags[c.code] });
      }
      setSaved(true);
    } catch {
      setError('save');
    } finally {
      // Always re-read: a partial failure must not leave the page claiming a
      // state the server doesn't have.
      await load();
      // Refresh the shared store so every CurrencySelect picks the new list up
      // without a page reload.
      await useCurrenciesStore.getState().reload();
      setSaving(false);
    }
  };

  if (!rows) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-5 h-72 w-full" />
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Coins className="h-5 w-5 text-fg-muted" aria-hidden />
              {t('currencies.title', { defaultValue: 'Currencies' })}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-fg-muted">
              {t('currencies.description', {
                defaultValue:
                  'Choose the currencies you trade in. Only these are offered in invoices, bills, vouchers, journal entries, price lists and the POS. Turning one off never changes existing documents — amounts already recorded in it keep their currency and decimals.',
              })}
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-border bg-bg-subtle px-3 py-1 text-xs font-semibold tabular-nums text-fg-muted">
            {t('currencies.enabledCount', {
              defaultValue: '{{on}} of {{total}} in use',
              on: enabledCount,
              total: rows.length,
            })}
          </span>
        </div>

        <div className="relative mt-4 max-w-sm">
          <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" aria-hidden />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('currencies.search', { defaultValue: 'Search currencies…' })}
            aria-label={t('currencies.search', { defaultValue: 'Search currencies…' })}
            className="ps-9"
          />
        </div>
      </Card>

      {visible.length === 0 ? (
        <Card className="p-10 text-center text-sm text-fg-muted">
          {t('currencies.noMatches', { defaultValue: 'No currencies match your search.' })}
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="flex flex-col divide-y divide-border">
            {visible.map((c) => {
              const isBase = c.code === base;
              // The base currency always reads as ON — it can't be switched off
              // here and the API refuses it, so never render it as unavailable.
              const on = Boolean(flags[c.code]) || isBase;
              const interactive = canWrite && !isBase;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`${c.code} — ${c.name}`}
                    disabled={!interactive}
                    onClick={() => toggle(c)}
                    className={[
                      'flex w-full items-center gap-3 px-4 py-3 text-start transition-colors duration-200',
                      interactive ? 'cursor-pointer hover:bg-bg-subtle/60' : 'cursor-not-allowed',
                      isBase ? 'bg-brand/[0.04]' : '',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'grid h-9 w-9 shrink-0 place-items-center rounded-lg font-mono text-xs font-bold transition-colors duration-200',
                        on ? 'bg-brand/15 text-brand' : 'bg-bg-muted text-fg-subtle',
                      ].join(' ')}
                      aria-hidden
                    >
                      {c.symbol.length <= 3 ? c.symbol : c.code}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-fg">{c.code}</span>
                        {isBase && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                            <Lock className="h-3 w-3" aria-hidden />
                            {t('currencies.base', { defaultValue: 'Base currency' })}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-fg-muted">
                        {c.name}
                        <span className="mx-1.5 text-fg-subtle">·</span>
                        {t('currencies.decimals', { defaultValue: '{{n}} decimals', n: c.exponent })}
                      </span>
                    </span>

                    {/* Switch — visual only; the whole row is the control. */}
                    <span
                      aria-hidden
                      className={[
                        'relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200',
                        on ? 'bg-brand' : 'bg-border',
                        interactive ? '' : 'opacity-70',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'absolute top-0.5 start-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out',
                          on ? 'ltr:translate-x-4 rtl:-translate-x-4' : '',
                        ].join(' ')}
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {error && (
        <p className="text-sm text-danger" role="alert">
          {error === 'load'
            ? t('currencies.loadError', { defaultValue: 'Could not load the currencies.' })
            : t('currencies.error', { defaultValue: 'Could not save the currencies.' })}
        </p>
      )}
      {saved && (
        <p className="text-sm text-success" role="status">
          {t('currencies.saved', { defaultValue: 'Currencies updated.' })}
        </p>
      )}

      {canWrite && dirty && (
        <div className="sticky bottom-4 z-10">
          <Card className="flex flex-wrap items-center justify-between gap-3 p-3 shadow-lg">
            <span className="text-sm text-fg-muted">
              {t('currencies.unsaved', { defaultValue: 'You have unsaved changes.' })}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" disabled={saving} onClick={() => setFlags(baseline)}>
                {tc('actions.cancel')}
              </Button>
              <Button type="submit" loading={saving}>{tc('actions.save')}</Button>
            </div>
          </Card>
        </div>
      )}
    </form>
  );
}
